import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import TextField from '../SanitizedTextField/SanitizedTextField';
import PropTypes from "prop-types";

const fieldLabelSx = {
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "#475569",
  mb: 0.75,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};

const readOnlyInputSx = {
  "& .MuiInputBase-root": {
    bgcolor: "#f1f5f9",
    borderRadius: "8px",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#334155",
    color: "#334155",
  },
};

const editableInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "#fff",
    "& fieldset": {
      borderColor: "#cbd5e1",
    },
    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#006ad1",
    },
  },
};

export default function ChangeWorkforcePasswordDialog({
  open,
  onClose,
  formik,
  apiError,
  showPassword,
  showConfirmPassword,
  onTogglePassword,
  onToggleConfirmPassword,
}) {
  const showError = (name) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="lineman-alert-popups lineman-form-dialog lineman-form-dialog--reference"
      PaperProps={{
        className: "lineman-form-dialog__paper lineman-form-dialog__paper--compact",
      }}
    >
      <DialogTitle
        component="div"
        className="dialog-title lineman-form-dialog__title"
      >
        <Typography component="h2" className="lineman-form-dialog__heading">
          Change Password
        </Typography>
        <IconButton
          id="clearPopup"
          aria-label="Close"
          onClick={onClose}
          size="small"
          className="lineman-form-dialog__close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="lineman-form-dialog__content">
        {apiError ? (
          <Alert severity="error" className="lineman-form-dialog__alert">
            {apiError}
          </Alert>
        ) : null}

        <Grid container spacing={2.5} className="lineman-form-dialog__grid">
          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>User Id.</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_userId"
              value={formik.values.iv_userId}
              disabled
              sx={readOnlyInputSx}
              className="lineman-form-dialog__designation"
            />
            {showError("iv_userId") ? (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                {showError("iv_userId")}
              </Typography>
            ) : null}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Remarks</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_remarks"
              value={formik.values.iv_remarks}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="off"
              inputProps={{ maxLength: 16 }}
              error={Boolean(showError("iv_remarks"))}
              helperText={showError("iv_remarks")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Password</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_password"
              type={showPassword ? "text" : "password"}
              value={formik.values.iv_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="off"
              inputProps={{ maxLength: 16 }}
              error={Boolean(showError("iv_password"))}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={onTogglePassword}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {showError("iv_password") ? (
              <Box
                component="p"
                sx={{ color: "#dc2626", fontSize: "0.75rem", mt: 0.5, mb: 0 }}
              >
                Password should:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li>have length between 8 to 16 characters.</li>
                  <li>contain at least 1 lower case letter.</li>
                  <li>contain at least 1 upper case letter.</li>
                  <li>contain at least 1 number.</li>
                  <li>contain at least 1 special character.</li>
                </Box>
              </Box>
            ) : null}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Password Confirmation</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_confirm_password"
              type={showConfirmPassword ? "text" : "password"}
              value={formik.values.iv_confirm_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="off"
              inputProps={{ maxLength: 16 }}
              error={Boolean(showError("iv_confirm_password"))}
              helperText={showError("iv_confirm_password")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={onToggleConfirmPassword}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className="lineman-form-dialog__actions">
        <Button
          type="button"
          variant="outlined"
          onClick={onClose}
          className="lineman-form-dialog__btn-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={formik.handleSubmit}
          className="lineman-form-dialog__btn-submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ChangeWorkforcePasswordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formik: PropTypes.object.isRequired,
  apiError: PropTypes.string,
  showPassword: PropTypes.bool.isRequired,
  showConfirmPassword: PropTypes.bool.isRequired,
  onTogglePassword: PropTypes.func.isRequired,
  onToggleConfirmPassword: PropTypes.func.isRequired,
};
